import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Row,
} from "reactstrap";
import BreadCrumb from "../../components/common/breadCrumb";
import TableContainer from "../../components/common/tableContainer";
import { InputPlaceHolder, validationMessages } from "../../utils/helpers/validationMasseges";
import * as Yup from "yup";
import { useFormik } from "formik";
import DeleteModal from "../../components/common/deleteModal";
import {
  addAliased,
  deleteAliased,
  editAliased,
  listOfAliasedData,
} from "../../api/alised";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { toast } from "react-toastify";
import NoResultsMessage from "../../components/noResultMessage";
import { checkStatusCodeSuccess, CommonLabel, errorHandler, SerialNoCol } from "../../utils/helpers/common";
import { BdeLabel } from "../../common/constant/Bde";
import { Link } from "react-router-dom";
import BaseButton from "../../components/BaseComponents/BaseButton";
import BaseModal from "../../components/BaseComponents/BaseModal";
import BaseInput from "../../components/BaseComponents/BaseInput";

const MasterTable = () => {
  document.title = BdeLabel.AliasedUser;
  const [alisedDataState, setAlisedDataState] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [aliased, setAliased] = useState([]);
  const [selectedDeleteId, setSelectedDeleteId] = useState();
  const [selectedEditId, setSelectedEditId] = useState();
  const [tableLoader, setTableLoader] = useState(false);
  const [modalLoader, setModalLoader] = useState(false);

  const aliasedDeleteFunc = (id) => {
    setSelectedDeleteId(id);
    setDeleteModal(true);
  };

  const aliasedDeleteTodo = () => {
    const data = { isDeleted: true };
    setModalLoader(true);
    deleteAliased(data, selectedDeleteId)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.data?.statusCode)) {
          fetchAliasedData();
          toast.success(res?.data?.message);
          setDeleteModal(false);
        } else {
          toast.error(res?.data?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setModalLoader(false);
      });
  };

  const fetchAliasedData = () => {
    setTableLoader(true);
    listOfAliasedData({
      modelName: "alisedNameModel",
      selectionCriteria: ["id", "alisedName"],
      condition: {
        isDeleted: false,
      },
    })
      .then((response) => {
        setAliased(response?.data);
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setTableLoader(false);
      });
  }

  useEffect(() => {
    fetchAliasedData();
  }, []);

  const aliasedColumn = useMemo(
    () => [
      SerialNoCol(),
      {
        Header: BdeLabel.AlisedName,
        accessor: "alisedName",
        filterable: false,
      },
      {
        Header: CommonLabel.Action,
        Cell: (cellProps) => {
          return (
            <div>
              <div className="hstack gap-2">
                <Link
                  id={`editTodo-${cellProps.cell.row.index}`}
                  className="btn btn-sm btn-soft-info edit-list"
                  onClick={() => editAlisedFunc(cellProps?.cell?.row?.original)}
                >
                  <i className="ri-pencil-fill align-bottom" />
                </Link>
                  <ReactTooltip
                    place="bottom"
                    variant="error"
                    content={CommonLabel.Update}
                    anchorId={`editTodo-${cellProps?.cell?.row?.index}`}
                  />
                <Link
                  id={`deleteTodo-${cellProps.cell.row.index}`}
                  className="btn btn-sm btn-soft-danger remove-list"
                  onClick={() =>
                    aliasedDeleteFunc(cellProps?.cell?.row?.original?.id)
                  }
                >
                  <i className="ri-delete-bin-5-fill align-bottom" />
                </Link>
                  <ReactTooltip
                    place="bottom"
                    variant="error"
                    content={CommonLabel.Delete}
                    anchorId={`deleteTodo-${cellProps?.cell?.row?.index}`}
                  />
              </div>
            </div>
          );
        },
      },
    ]
  );

  const addAlisedData = () => {
    setAlisedDataState(!alisedDataState);
    AlisedToggle();
  };

  const AlisedToggle = useCallback(() => {
    if (alisedDataState) {
      setAlisedDataState(false);
      validation.resetForm();
      setIsEdit(false);
      setSelectedEditId("");
    } else {
      setAlisedDataState(true);
    }
  }, [alisedDataState]);

  const editAlisedFunc = (data) => {
    // Here we have to do the Edit Operation or Api Calling
    setAlisedDataState(!alisedDataState);
    setIsEdit(true);
    AlisedToggle();
    setSelectedEditId(data);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      alisedName: (selectedEditId && selectedEditId?.alisedName) || "",
    },
    validationSchema: Yup.object({
      alisedName: Yup.string().required(validationMessages.required(BdeLabel.AlisedName)),
    }),
    onSubmit: (values) => {
      setModalLoader(true);
      if (!isEdit) {
        const payload = {
          alisedName: values.alisedName,
        };
        addAliased(payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.data?.statusCode)) {
              fetchAliasedData();
              validation.resetForm();
              toast.success(res?.data?.message);
              setAlisedDataState(false);
            } else {
              toast.error(res?.data?.message);
            }
          })
          .catch((err) => {
            return err;
          })
          .finally(() => {
            setModalLoader(false);
          });
      } else {
        editAliased(selectedEditId?.id, { ...values })
          .then((res) => {
            if (checkStatusCodeSuccess(res?.data?.statusCode)) {
              validation.resetForm();
              fetchAliasedData();
              toast.success(res?.data?.message);
              setSelectedEditId("");
              setIsEdit(false);
              setAlisedDataState(false);
            } else {
              toast.error(res?.data?.message);
            }
          })
          .catch((err) => {
            errorHandler(err);
            return err;
          })
          .finally(() => {
            setModalLoader(false);
          });
      }
    },
  });

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => aliasedDeleteTodo()}
        onCloseClick={() => setDeleteModal(false)}
        loader={modalLoader}
      />
      <div className="page-content">
          <Container fluid>
            <BreadCrumb title="Alise User" pageTitle="HRMS" />
            <Row className="mb-2">
              <div className="col-sm order-3 order-sm-2 mt-3 mt-sm-0">
                <h5 className="fw-semibold mt-2">{BdeLabel.AliasedUserName}</h5>
              </div>
              <div className="col-auto order-2 order-sm-3 ms-auto">
                <div className="hstack gap-2">
                  <BaseButton
                    color="primary"
                    type="button"
                    onClick={() => addAlisedData()}
                  >
                    <i className="ri-add-line align-bottom"></i>{" "}
                    {BdeLabel.AddAliasedUser}
                  </BaseButton>
                </div>
              </div>
            </Row>

            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    {(aliased?.length > 0 || tableLoader) ? (
                      <TableContainer
                        columns={aliasedColumn}
                        data={aliased}
                        isAddUserList={false}
                        customPageSize={5}
                        isGlobalFilter={true}
                        tableLoader={tableLoader}
                        divClass="table-responsive table-card mb-1"
                        tableClass="align-middle table-nowrap"
                        theadClass="table-light text-muted"
                      />
                    ) : (
                      <NoResultsMessage />
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
      </div>

      <BaseModal
        open={alisedDataState}
        onClose={() => {
          setAlisedDataState(false);
          setIsEdit(false);
          setSelectedEditId("");
          validation.resetForm();
        }}
        toggle={AlisedToggle}
        className="zoomIn"
        title={isEdit ? BdeLabel.UpdateAliasedUser : BdeLabel.AddAliasedUser}
        bodyClassName="p-4"
        submit={validation.handleSubmit}
        submitLabel={isEdit ? CommonLabel.Update : CommonLabel.Submit}
        submitLabelColor="primary"
        closeLabel={CommonLabel.Close}
        closeLabelColor="ghost-success"
        loader={modalLoader}
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <Row>
            <Col lg={12}>
              <Row>
                <BaseInput
                  placeholder={InputPlaceHolder(BdeLabel.AliasedUserName)}
                  label={BdeLabel.AliasedUserName}
                  type="text"
                  name="alisedName"
                  onChange={validation.handleChange}
                  handleBlur={validation.handleBlur}
                  value={validation.values.alisedName}
                  error={validation.errors.alisedName}
                  touched={validation.touched.alisedName}
                  required
                />
              </Row>
            </Col>
          </Row>
        </Form>
      </BaseModal>
    </>
  );
};

export default MasterTable;
